import React from 'react'
import { FaArrowRight } from 'react-icons/fa'
import {
  DefinitionTerm,
  Section,
  SectionHeader,
  SectionBody,
  Table,
  TableColumn,
  TableRecord,
  DefinitionListItem,
} from '@apexinternal/ui-components'
import { Icon, Link } from '@chakra-ui/react'
import { isEmpty } from 'lodash'
import { Activity, RoutesEnum } from '../../types'
import {
  ALT_DATE_FORMAT,
  capitalizeWord,
  formatDate,
  getPastDate,
  getToday,
  LONG_DATE_FORMAT,
  numberToDollars,
} from '../../utils'

export interface RecentActivityProps {
  accountNumber: string
  activities?: Activity[]
  processDate?: string
}

const assembleColumn = (
  label: string,
  recordField: string,
  width: number,
  isNumeric?: boolean,
  activities?: {
    value: string
    label: string
  }[],
  cellRenderer?: ({ value }: { value: any }) => JSX.Element
): TableColumn => ({
  key: recordField,
  label,
  recordField,
  width,
  isNumeric,
  isCentered: false,
  isSortable: false,
  ...activities,
  ...cellRenderer,
})

const RecentActivityTable = ({
  accountNumber,
  activities,
  processDate,
}: RecentActivityProps) => {
  const longToday = getToday().toFormat(LONG_DATE_FORMAT)
  const today = getToday().toString()
  const startDate = processDate
    ? formatDate(getPastDate(processDate, 5), LONG_DATE_FORMAT)
    : formatDate(getPastDate(today, 5), LONG_DATE_FORMAT)
  const endDate = processDate
    ? formatDate(processDate, LONG_DATE_FORMAT)
    : longToday

  const columns: TableColumn[] = [
    assembleColumn('Type', 'activityType', 100),
    assembleColumn('Trade Date', 'tradeDate', 75),
    assembleColumn('Settle Date', 'settleDate', 75),
    assembleColumn('Tag #', 'tagNumber', 50),
    assembleColumn('Time Stamp', 'timestamp', 75),
    assembleColumn('Symbol', 'symbol', 75),
    assembleColumn('Description', 'description', 300),
    assembleColumn('Trade Action', 'tradeAction', 75),
    assembleColumn('Qty', 'quantity', 50, true),
    assembleColumn('Price', 'price', 50, true),
    assembleColumn('Net Amount', 'netAmount', 75, true),
  ]

  const data: TableRecord[] =
    activities?.map(
      (
        {
          accountNumber,
          activityType,
          tradeDate,
          settleDate,
          tagNumber,
          timestamp,
          symbol,
          descriptionLines,
          tradeAction,
          quantity,
          transferDirection,
          price,
          netAmount,
        },
        index
      ) => {
        return {
          key: `${accountNumber}_${index}`,
          activityType:
            activityType
              ?.split('_')
              .map(word => capitalizeWord(word))
              .join(' ') || '—',
          tradeDate: formatDate(tradeDate, ALT_DATE_FORMAT),
          settleDate: formatDate(settleDate, ALT_DATE_FORMAT),
          tagNumber: tagNumber || '—',
          timestamp: formatDate(timestamp, ALT_DATE_FORMAT),
          symbol: symbol || '—',
          description: `${descriptionLines.join(' — ')}` || '—',
          tradeAction:
            tradeAction
              ?.split('_')
              .map(word => capitalizeWord(word))
              .join(' ') || '—',
          quantity: quantity || 0,
          price: numberToDollars(price || 0),
          netAmount:
            transferDirection === 'OUTGOING'
              ? `-${numberToDollars(netAmount || 0)}`
              : numberToDollars(netAmount || 0),
        }
      }
    ) || []

  return (
    <Section data-testid="recent-activity" pt="2rem" width="100%">
      <SectionHeader
        tools={
          <Link
            href={`/${RoutesEnum.Accounts}/${accountNumber}/activity`}
            sx={{
              ':hover': {
                color: 'apex.white',
              },
              color: 'apex.white',
              fontWeight: 'regular',
            }}
          >
            Activity Details <Icon as={FaArrowRight} color="apex.white" />
          </Link>
        }
      >
        Recent Activity
      </SectionHeader>
      <SectionBody>
        {data && !isEmpty(data) ? (
          <Table
            data-testid="activity-table"
            columns={columns}
            data={data}
            defaultSort={{ field: 'tradeDate', ascending: false }}
          />
        ) : (
          <DefinitionListItem>
            <DefinitionTerm>
              {`There are no recent activities to display between ${startDate} and ${endDate}.`}
            </DefinitionTerm>
          </DefinitionListItem>
        )}
      </SectionBody>
    </Section>
  )
}

export default RecentActivityTable
